import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarDayScene, CalendarDayContext } from '@/contexts/CalendarDayContext';
import { TimerManagerProvider, TimerManagerContext } from '@/contexts/TimerManagerContext';
import { useCharacter } from '@/contexts/CharacterContext';
import { useStars } from '@/contexts/StarContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import {
  EmbeddedCompanion,
  useCompanionMoment,
  useNamedCompanionSpeech,
} from '@/components/character';
import {
  ChildActivityCard,
  StarCounter,
  DailyMoodCheck,
  useChildTokens,
  NextUpCard,
  TransitionWarning,
  useNpfNudgePrefs,
  getNextActivity,
} from '@/components/child';
import { dateLocales } from '@/i18n/dateLocales';
import VoiceActivityCreator from '@/components/voice/VoiceActivityCreator';
import CreateActivityFab from '@/components/layout/CreateActivityFab';

/**
 * ChildHome — barnvyns "Idag"-skärm (prototypens HOME). Tidsbaserad hälsning via
 * den inbäddade kompanjonen, mood-check, stjärnräknare, dagens aktivitetslista
 * och en FAB för att skapa ny aktivitet. Riktig data via CalendarContext.
 */

const greetingKeyForHour = (hour: number): string => {
  if (hour < 11) return 'child.companion.greeting.morning';
  if (hour < 17) return 'child.companion.greeting.day';
  return 'child.companion.greeting.evening';
};

const ChildHomeInner = () => {
  const { t, i18n } = useTranslation();
  const tokens = useChildTokens();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { instances, date } = useContext(CalendarDayContext);
  const { renderTime } = useContext(TimerManagerContext);
  const { nextUpEnabled, transitionWarningEnabled } = useNpfNudgePrefs();
  const { setMood, setCompanionSpeech } = useCharacter();
  const { balance } = useStars();

  // Nästa aktivitet för igångsättningsstöd + övergångsförvarning (Pipeline 3).
  // Beräknas EN gång här och delas mellan TransitionWarning och NextUpCard.
  // renderTime tickar varje minut (TimerManagerProvider) så förvarningen räknas ned.
  const nextUp = useMemo(() => getNextActivity(instances, renderTime), [instances, renderTime]);

  const greetingKey = useMemo(() => greetingKeyForHour(new Date().getHours()), []);
  // Kompisen tilltalar BARNET vid namn (user.firstName) när ett giltigt namn
  // finns — annars den namnlösa varianten. Kompisens EGET namn får aldrig
  // matas in här (då hälsade kompisen på sig själv).
  const { speechKey, speechVars } = useNamedCompanionSpeech(greetingKey);
  // Tidsbaserad hälsning (happy → idle). Nollställs vid unmount.
  useCompanionMoment('happy', speechKey, speechVars);

  const locale = dateLocales[i18n.language] ?? dateLocales.en;
  const weekday = format(date, 'EEEE', { locale });
  const dateStr = format(date, 'd MMMM yyyy', { locale });

  // Håll tack-timern i ref så den rensas vid nytt val OCH vid unmount (annars
  // setMood/setCompanionSpeech på avmonterad vy om man byter sida inom 1.5s).
  const thanksTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (thanksTimerRef.current !== null) {
        window.clearTimeout(thanksTimerRef.current);
      }
    },
    [],
  );

  // När barnet väljer humör: kompanjonen tackar kort (happy 1.5s → idle).
  const handleMoodPicked = useCallback(() => {
    setMood('happy');
    setCompanionSpeech(t('child.mood.thanks') ?? '');
    if (thanksTimerRef.current !== null) {
      window.clearTimeout(thanksTimerRef.current);
    }
    thanksTimerRef.current = window.setTimeout(() => {
      setMood('idle');
      // Samma variant som vid mount — namnet får inte försvinna efter tacket.
      setCompanionSpeech(t(speechKey, speechVars) ?? '');
      thanksTimerRef.current = null;
    }, 1500);
  }, [setMood, setCompanionSpeech, t, speechKey, speechVars]);

  return (
    <Container sx={{ pb: 12 }}>
      <Stack spacing={2} pt={1.5}>
        {/* Header: datum + stjärnräknare */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: '1.1rem',
                color: tokens.text,
                textTransform: 'capitalize',
              }}
            >
              {weekday}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: tokens.textSoft, fontWeight: 600 }}>
              {dateStr}
            </Typography>
          </Box>
          <StarCounter count={balance.totalEarnedStars} />
        </Box>

        {/* Kompanjon-sektion (stor) med mood-check som children */}
        <EmbeddedCompanion variant="home" accent="primary">
          <DailyMoodCheck onPicked={handleMoodPicked} />
        </EmbeddedCompanion>

        {/* NPF-nudges (Pipeline 3): övergångsförvarning + nästa-upp-hero. Tystnar
            automatiskt vid tom/klar/missad dag (nextUp = null). */}
        {transitionWarningEnabled && nextUp && (
          <TransitionWarning result={nextUp} renderTime={renderTime} />
        )}
        {nextUpEnabled && nextUp && <NextUpCard result={nextUp} />}

        {/* Dagens lista */}
        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: tokens.text }}>
          📋 {t('child.home.todayTitle')}
        </Typography>
        {instances.length === 0 ? (
          <Box
            sx={{
              bgcolor: tokens.card,
              border: '1px dashed',
              borderColor: tokens.border,
              borderRadius: '14px',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: tokens.textSoft }}>
              {t('child.home.empty')}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.25}>
            {instances.map((instance) => (
              <ChildActivityCard key={instance.activityInstanceId} instance={instance} />
            ))}
          </Stack>
        )}
      </Stack>

      {/* FAB: skapa aktivitet + röst (gemensam position via CreateActivityFab) */}
      <CreateActivityFab onVoiceClick={() => setVoiceOpen(true)} />

      <VoiceActivityCreator
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSaved={() => setVoiceOpen(false)}
      />
    </Container>
  );
};

const ChildHome = () => {
  const effectiveChildMode = useEffectiveChildMode();
  // Säkerhet: barn-sidan ska aldrig renderas i vuxenläge (ChildRouteSwitch gör
  // detta redan, men gaten upprepas här som invariant-skydd).
  if (!effectiveChildMode) {
    return null;
  }
  return (
    <CalendarDayScene date={new Date()}>
      {/* TimerManagerProvider ger en renderTime som tickar varje minut, så
          övergångsförvarningen (Pipeline 3) räknas ned utan omladdning. */}
      <TimerManagerProvider>
        <ChildHomeInner />
      </TimerManagerProvider>
    </CalendarDayScene>
  );
};

export default ChildHome;
